import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventApi, EventClickArg, EventContentArg, EventDropArg, EventMountArg } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';

import type { Activity, ActivityType } from '@/api/activities';
import type { Fixture } from '@/api/fixtures';
import type { Location } from '@/api/locations';
import { resolve as resolveTeamColor } from '@/lib/teamColor';
import { typeTone } from '@/lib/activityType';
import { matchDisplay, scoreLabel, type MatchDisplay } from '@/lib/matchDisplay';
import { MatchSides } from '@/components/schedule/MatchSides';

export type ScheduleView = 'week' | 'list' | 'month';

// BusinessHoursRule matches FullCalendar's recurring businessHours shape.
// daysOfWeek uses Sunday=0..Saturday=6 (FullCalendar's convention); the
// caller is responsible for mapping any other day numbering before
// passing in. Outside these windows the grid renders muted, signalling
// the slot isn't part of the filtered location's opening hours.
export interface BusinessHoursRule {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
}

// BackgroundEvent is a FullCalendar event with display='background' —
// rendered as a coloured day-cell or grid-region rather than a normal
// activity card. Used for closed-day availability exceptions: a tighter
// visual block than the regular off-hours background. The CSS rule that
// styles `.fc-availability-closed` lives in styles/global.css.
export interface BackgroundEvent {
  start: string;
  end: string;
  display: 'background';
  classNames?: string[];
}

// AvailabilityBlockReason identifies why a calendar interaction was
// rejected — covers both availability (opening hours, closed-day
// exceptions) and overlap (same team / same zone) checks. Surfaced via
// the bottom-of-screen notice so the user knows what to fix.
export type AvailabilityBlockReason =
  | 'closed_exception'
  | 'outside_hours'
  | 'team_conflict'
  | 'zone_conflict';

// availabilityBlockReason — returns the reason a [start, end) range
// isn't bookable, or null when it is. Used to gate click-to-create,
// drag-and-drop, and resize; also used to render the per-action notice.
//
// When `businessHours` is undefined (no location filtered, or the
// location has no declared hours), every range is allowed — same
// always-open default as the server-side CheckAvailability.
export function availabilityBlockReason(
  start: Date,
  end: Date,
  businessHours: BusinessHoursRule[] | undefined,
  backgroundEvents: BackgroundEvent[] | undefined,
): AvailabilityBlockReason | null {
  if (backgroundEvents && backgroundEvents.length > 0) {
    for (const bg of backgroundEvents) {
      if (!bg.classNames?.includes('fc-availability-closed')) continue;
      const bgStart = new Date(bg.start);
      const bgEnd = new Date(bg.end);
      // Half-open overlap test — touching endpoints don't conflict.
      if (start < bgEnd && end > bgStart) return 'closed_exception';
    }
  }
  if (!businessHours || businessHours.length === 0) return null;
  // Activities typically sit within a single weekday. Cross-midnight
  // ranges are rare for sports schedules; we'd need to walk per-day
  // for those. Single-day check is the 95% case.
  const dow = start.getDay();
  const rules = businessHours.filter((r) => r.daysOfWeek.includes(dow));
  if (rules.length === 0) return 'outside_hours';
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes();
  for (const r of rules) {
    const [rsH = 0, rsM = 0] = r.startTime.split(':').map(Number);
    const [reH = 0, reM = 0] = r.endTime.split(':').map(Number);
    const ruleStart = rsH * 60 + rsM;
    const ruleEnd = reH * 60 + reM;
    if (startMin >= ruleStart && endMin <= ruleEnd) return null;
  }
  return 'outside_hours';
}

// isInsideAvailability — boolean shorthand. Kept so callers that only
// care about the predicate (not the reason) stay terse.
export function isInsideAvailability(
  start: Date,
  end: Date,
  businessHours: BusinessHoursRule[] | undefined,
  backgroundEvents: BackgroundEvent[] | undefined,
): boolean {
  return availabilityBlockReason(start, end, businessHours, backgroundEvents) === null;
}

interface Props {
  view: ScheduleView;
  date: Date;
  activities: Activity[];
  allActivities?: Activity[];
  locations: Location[];
  // Fixture per `match` activity id (see useActivityFixtures). Matches with an
  // opponent render as a game — both sides home-first with their own crest,
  // home/away, score. Omit, or leave a match out, and it falls back to the
  // generic activity card.
  fixtures?: Map<string, Fixture>;
  // The club's own logo URL (see useOrgCrest), shown next to our team on a
  // fixture. Null / omitted renders our side with a monogram instead.
  orgCrestUrl?: string | null;
  // When set (typically because the user filtered to exactly one
  // location), the calendar greys out time slots outside these rules.
  // Pass `undefined` to keep the whole grid neutral — no rules = always
  // open in our model.
  businessHours?: BusinessHoursRule[];
  // Extra background events merged into the FullCalendar event list. Used
  // for closed-day exceptions so they paint the day even on top of the
  // businessHours muting. Optional; omit when there are none.
  backgroundEvents?: BackgroundEvent[];
  onActivityClick: (id: string) => void;
  onReschedule: (id: string, startsAt: Date, endsAt: Date) => Promise<void>;
  onDateClick?: (when: Date) => void;
  // Fires when the user attempts an interaction (click-to-create,
  // drop, resize) that the availability gate rejects. Parent renders
  // a transient notice with the reason. NOT fired during live drag
  // preview — only when the user commits an action that gets blocked,
  // so the message reflects intent.
  onBlocked?: (reason: AvailabilityBlockReason) => void;
}

const VIEW_TO_FC: Record<ScheduleView, string> = {
  week: 'timeGridWeek',
  list: 'listWeek',
  month: 'dayGridMonth',
};

export function ScheduleCalendar({
  view,
  date,
  activities,
  allActivities,
  locations,
  fixtures,
  orgCrestUrl,
  businessHours,
  backgroundEvents,
  onActivityClick,
  onReschedule,
  onDateClick,
  onBlocked,
}: Props) {
  const { t, i18n } = useTranslation();
  const ref = useRef<FullCalendar | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Drag-block tracking. eventAllow blocks the drop visually (no
  // eventDrop fires), so without this we never see a reason to surface
  // the notice on a blocked-drag attempt. The ref captures the latest
  // rejection reason observed during the drag; eventDragStop /
  // eventResizeStop fire it once when the user releases.
  const lastBlockedReasonRef = useRef<AvailabilityBlockReason | null>(null);

  useEffect(() => {
    const api = ref.current?.getApi();
    if (!api) return;
    api.gotoDate(date);
  }, [date]);

  useEffect(() => {
    const api = ref.current?.getApi();
    if (!api) return;
    api.changeView(VIEW_TO_FC[view]);
  }, [view]);

  const events = useMemo(() => {
    const locMap = new Map<string, Location>();
    for (const loc of locations) locMap.set(loc.id, loc);

    return activities.map((a) => {
      const loc = a.location_id ? locMap.get(a.location_id) : undefined;
      const zones = loc
        ? [...loc.zones].sort((x, y) => x.sort_order - y.sort_order)
        : [];
      const zoneIdx = a.zone_id ? zones.findIndex((z) => z.id === a.zone_id) : -1;
      const zoneTotal = zones.length;
      const teamNames = (a.teams ?? []).map((t) => t.name).filter(Boolean);

      const tone = typeTone(a.activity_type);
      const team = a.teams?.[0];
      const teamDot = team ? resolveTeamColor(team.name, team.color).dot : '';
      // A match with an opponent set reads as a game; everything else (and a
      // TBD fixture) keeps the team-name card.
      const match = matchDisplay(fixtures?.get(a.id), {
        name: team?.name || a.title,
        logoUrl: orgCrestUrl ?? null,
      });
      const base = {
        id: a.id,
        title: a.title,
        start: a.starts_at,
        end: a.ends_at,
        extendedProps: {
          detached: !!a.detached_at,
          team_ids: a.team_ids,
          team_names: teamNames,
          activity_type: a.activity_type,
          location_id: a.location_id ?? null,
          zone_id: a.zone_id ?? null,
          location_name: loc?.name ?? null,
          zone_name: zoneIdx >= 0 ? (zones[zoneIdx]?.name ?? null) : null,
          zone_left: zoneIdx >= 0 && zoneTotal > 0 ? (zoneIdx / zoneTotal) * 100 : null,
          zone_right: zoneIdx >= 0 && zoneTotal > 0 ? ((zoneTotal - zoneIdx - 1) / zoneTotal) * 100 : null,
          // Stash colors so handleEventDidMount can apply them as CSS
          // custom properties on the harness — keeps the card layout
          // driven by tokens, not inline backgrounds on .fc-event.
          tone_bg: tone.bg,
          tone_accent: tone.accent,
          team_dot: teamDot,
          match,
        },
        // Use the type tint as the FC background; the .fc-event border-
        // color carries the team color and we render the left-border
        // visual via a CSS box-shadow inset. Keeping FC's color knobs
        // pointed at our tokens means listWeek and dayGridMonth pick up
        // the same scheme automatically.
        backgroundColor: tone.bg,
        borderColor: teamDot || tone.accent,
        textColor: 'var(--color-foreground)',
      };
      return base;
    });
  }, [activities, locations, fixtures, orgCrestUrl]);

  // Custom event rendering — the default FullCalendar chip in timeGrid is
  // tightly cropped by our pill padding so titles + team names get
  // clipped. We render an explicit layout per view:
  //   - listWeek: title + subtitle on one row (existing behaviour).
  //   - timeGridWeek: time → title → team → location, stacked. The CSS
  //     auto-hides each line if the card is too short for it (the team /
  //     location lines fade out below ~32 / ~52 px card height).
  //   - dayGridMonth: time chip + title on one line (no room for more).
  const renderEventContent = useCallback((arg: EventContentArg) => {
    const ext = arg.event.extendedProps as {
      team_names?: string[];
      location_name?: string | null;
      zone_name?: string | null;
      activity_type?: ActivityType;
      tone_accent?: string;
      team_dot?: string;
      match?: MatchDisplay | null;
    };
    const teamNames = ext.team_names ?? [];
    const locationParts = [ext.location_name, ext.zone_name].filter(Boolean) as string[];
    const typeLabel = ext.activity_type
      ? t(`schedule.activity.type.option.${ext.activity_type}`)
      : '';
    // A fixture replaces the team line with the matchup and puts home/away in
    // front of the venue, so a game reads as a game at a glance.
    const match = ext.match ?? null;
    const homeAway = match
      ? t(`schedule.match.homeAway.${match.isHome ? 'home' : 'away'}`)
      : '';
    const score = match ? scoreLabel(match) : null;

    if (arg.view.type === 'listWeek') {
      const subtitleParts: string[] = [];
      if (match) {
        subtitleParts.push(homeAway);
        if (match.competition) subtitleParts.push(match.competition);
      } else if (teamNames.length) {
        subtitleParts.push(teamNames.join(', '));
      }
      if (locationParts.length) subtitleParts.push(locationParts.join(' · '));
      return (
        <div className="flex w-full items-baseline justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            {match ? (
              <MatchSides match={match} size={20} className="font-medium" />
            ) : (
              <span className="truncate font-medium">{arg.event.title}</span>
            )}
            {score && <span className="shrink-0 font-heading font-bold tabular-nums">{score}</span>}
          </span>
          {subtitleParts.length > 0 && (
            <span className="truncate text-xs text-foreground/60">
              {subtitleParts.join('  ·  ')}
            </span>
          )}
        </div>
      );
    }

    if (arg.view.type === 'timeGridWeek') {
      // Layout: time top-left + type eyebrow top-right; team name with
      // team-color dot; location below. Container queries in the CSS
      // collapse the location line first, then the team line, as the
      // card shrinks past 50/35px tall.
      //
      // A fixture keeps that exact skeleton — same classes, so the collapse
      // rules still apply — but swaps the team dot for the opponent's crest,
      // the team name for "us – them", and prefixes the meta line with
      // home/away. The score sits at the end of the matchup line once final.
      const metaParts = match ? [homeAway, ...locationParts] : locationParts;
      if (match?.competition) metaParts.push(match.competition);
      return (
        <div className="fc-event-stack flex h-full w-full flex-col gap-0.5 overflow-hidden px-2 py-1 text-[11px] leading-tight">
          <div className="fc-event-time-row flex items-baseline justify-between gap-2">
            <span className="truncate font-heading text-[10px] font-semibold tabular-nums text-foreground/60">
              {arg.timeText}
            </span>
            {typeLabel && (
              <span
                className="shrink-0 font-heading text-[9px] font-bold uppercase tracking-wider"
                style={{ color: ext.tone_accent }}
              >
                {typeLabel}
              </span>
            )}
          </div>
          {match ? (
            // Stacked: a day column is a seventh of the grid, far too narrow to
            // fit both club names on one line.
            <div className="flex items-start gap-1.5">
              <MatchSides
                match={match}
                size={16}
                orientation="stacked"
                className="font-heading text-[12px] font-bold text-foreground"
              />
              {score && (
                <span className="ml-auto shrink-0 font-heading text-[12px] font-bold tabular-nums">
                  {score}
                </span>
              )}
            </div>
          ) : (
            teamNames.length > 0 && (
              <div className="flex items-center gap-1.5">
                {ext.team_dot && (
                  <span
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ background: ext.team_dot }}
                    aria-hidden
                  />
                )}
                <span className="truncate font-heading text-[12px] font-bold text-foreground">
                  {teamNames.join(', ')}
                </span>
              </div>
            )
          )}
          {metaParts.length > 0 && (
            <div className="fc-event-meta truncate text-foreground/60">
              {metaParts.join(' · ')}
            </div>
          )}
        </div>
      );
    }

    if (arg.view.type === 'dayGridMonth') {
      // One line only — the crests stand in for the team dot and the matchup
      // for the title. No room for venue or home/away here.
      return (
        <div className="flex w-full items-center gap-1 truncate">
          {!match && ext.team_dot && (
            <span
              className="inline-block size-2 shrink-0 rounded-full"
              style={{ background: ext.team_dot }}
              aria-hidden
            />
          )}
          <span className="shrink-0 font-heading text-[10px] font-semibold tabular-nums text-foreground/60">
            {arg.timeText}
          </span>
          {match ? (
            <MatchSides match={match} size={14} className="font-medium" />
          ) : (
            <span className="truncate font-medium">{arg.event.title}</span>
          )}
        </div>
      );
    }

    return undefined;
  }, [t]);

  // Re-apply zone CSS to harneses whenever events change (e.g. when locations
  // load after the initial mount). handleEventDidMount only fires at mount, so
  // we also run a DOM pass after every events update.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const eventsMap = new Map(events.map((e) => [e.id, e.extendedProps]));
    for (const harness of container.querySelectorAll<HTMLElement>(
      '.fc-timegrid-event-harness[data-event-id]',
    )) {
      const id = harness.dataset.eventId;
      if (!id) continue;
      const props = eventsMap.get(id);
      if (props) applyZoneToHarness(harness, props);
    }
  }, [events]);

  async function handleDrop(arg: EventDropArg) {
    if (!arg.event.start || !arg.event.end) {
      arg.revert();
      return;
    }
    // Availability is the outer gate — eventAllow already blocks the drop
    // visually, but a second post-drop check belt-and-braces against any
    // future code path that bypasses eventAllow (programmatic drops, etc).
    const blocked = availabilityBlockReason(arg.event.start, arg.event.end, businessHours, backgroundEvents);
    if (blocked) {
      onBlocked?.(blocked);
      arg.revert();
      return;
    }
    // Post-drop conflict safety net. The live eventOverlap callback only
    // sees rendered events; activities hidden by the location/team
    // filter still need to block. When they trigger here, surface the
    // reason via the notice so the user understands the silent revert.
    const conflict = hasLocationConflictReason(arg.event.id, arg.event.start, arg.event.end, arg.event.extendedProps, allActivities ?? activities);
    if (conflict) {
      onBlocked?.(conflict);
      arg.revert();
      return;
    }
    try {
      await onReschedule(arg.event.id, arg.event.start, arg.event.end);
    } catch {
      arg.revert();
    }
  }

  async function handleResize(arg: EventResizeDoneArg) {
    if (!arg.event.start || !arg.event.end) {
      arg.revert();
      return;
    }
    const blocked = availabilityBlockReason(arg.event.start, arg.event.end, businessHours, backgroundEvents);
    if (blocked) {
      onBlocked?.(blocked);
      arg.revert();
      return;
    }
    const conflict = hasLocationConflictReason(arg.event.id, arg.event.start, arg.event.end, arg.event.extendedProps, allActivities ?? activities);
    if (conflict) {
      onBlocked?.(conflict);
      arg.revert();
      return;
    }
    try {
      await onReschedule(arg.event.id, arg.event.start, arg.event.end);
    } catch {
      arg.revert();
    }
  }

  function handleClick(arg: EventClickArg) {
    arg.jsEvent.preventDefault();
    onActivityClick(arg.event.id);
  }

  function handleEventDidMount(arg: EventMountArg) {
    const harness = arg.el.parentElement;
    if (!harness) return;
    // Tag the harness with the event ID so the useEffect zone-reapply pass
    // can find it by event ID after extendedProps update without remount.
    (harness as HTMLElement).dataset.eventId = arg.event.id;
    applyZoneToHarness(harness as HTMLElement, arg.event.extendedProps as Record<string, unknown>);
  }

  return (
    <div ref={containerRef} className="schedule-calendar">
      <FullCalendar
        ref={ref}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView={VIEW_TO_FC[view]}
        initialDate={date}
        firstDay={1 /* Monday */}
        locale={i18n.resolvedLanguage ?? 'en'}
        height="auto"
        slotMinTime="06:00:00"
        slotMaxTime="23:00:00"
        allDaySlot={false}
        nowIndicator
        editable
        eventStartEditable
        eventDurationEditable
        droppable={false}
        // Inline closure so the conflict check can also stash the reason
        // into lastBlockedReasonRef for eventDragStop / eventResizeStop
        // to surface in the notice. The pure logic still lives in
        // overlapBlockReason — this is just the "block AND remember why"
        // shim FullCalendar's boolean eventOverlap expects.
        eventOverlap={(stillEvent, movingEvent) => {
          const reason = overlapBlockReason(stillEvent, movingEvent);
          if (reason) lastBlockedReasonRef.current = reason;
          return reason === null;
        }}
        headerToolbar={false}
        dayHeaderContent={(arg) => capitaliseFirst(arg.text)}
        events={backgroundEvents ? [...events, ...backgroundEvents] : events}
        // Conditional spread so we don't pass `undefined`. FullCalendar's
        // BusinessHoursInput doesn't accept undefined; omitting the prop
        // gives a neutral grid, which is what we want.
        {...(businessHours ? { businessHours } : {})}
        // eventAllow gates the live drag/resize preview — returning false
        // makes the drop target visually rejected so the user doesn't
        // commit to an out-of-hours move. Side effect: records the
        // latest block reason so eventDragStop / eventResizeStop can
        // surface the notice if the user releases over a rejected zone.
        eventAllow={(drop) => {
          const reason = availabilityBlockReason(drop.start, drop.end, businessHours, backgroundEvents);
          lastBlockedReasonRef.current = reason;
          return reason === null;
        }}
        eventDragStart={() => {
          lastBlockedReasonRef.current = null;
        }}
        eventDragStop={() => {
          if (lastBlockedReasonRef.current) {
            onBlocked?.(lastBlockedReasonRef.current);
            lastBlockedReasonRef.current = null;
          }
        }}
        eventResizeStart={() => {
          lastBlockedReasonRef.current = null;
        }}
        eventResizeStop={() => {
          if (lastBlockedReasonRef.current) {
            onBlocked?.(lastBlockedReasonRef.current);
            lastBlockedReasonRef.current = null;
          }
        }}
        eventClick={handleClick}
        eventDrop={handleDrop}
        eventResize={handleResize}
        dateClick={(arg) => {
          // Block click-to-create in greyed regions. Default activity
          // length is 1h — match the duration the create flow uses
          // when seeding from a calendar click.
          const probeEnd = new Date(arg.date.getTime() + 60 * 60 * 1000);
          const blocked = availabilityBlockReason(arg.date, probeEnd, businessHours, backgroundEvents);
          if (blocked) {
            onBlocked?.(blocked);
            return;
          }
          onDateClick?.(arg.date);
        }}
        eventDidMount={handleEventDidMount}
        eventContent={renderEventContent}
      />
    </div>
  );
}

function capitaliseFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function applyZoneToHarness(harness: HTMLElement, props: Record<string, unknown>) {
  const zone_left = props.zone_left as number | null;
  const zone_right = props.zone_right as number | null;
  if (zone_left === null || zone_right === null) {
    delete harness.dataset.zone;
    harness.style.removeProperty('--zone-left');
    harness.style.removeProperty('--zone-right');
    return;
  }
  harness.dataset.zone = '1';
  harness.style.setProperty('--zone-left', `${zone_left}%`);
  harness.style.setProperty('--zone-right', `${zone_right}%`);
}

// Called during drag (one call per other-event overlap candidate).
// Returns the reason an overlap is rejected, or null when overlap is OK.
// Same predicate the original boolean canEventsOverlap encoded; refactor
// preserves "same team in any time overlap = blocked, OR same location+
// zone regardless of team = blocked". Different teams at different
// locations still coexist at the same time.
//
// Surfaced as a string reason rather than a boolean so the notice can
// tell the user which constraint they hit — important when the
// conflicting activity is hidden by a location/zone filter and the
// "not allowed" cursor alone leaves them guessing.
export function overlapBlockReason(
  stillEvent: EventApi,
  movingEvent: EventApi | null,
): AvailabilityBlockReason | null {
  const stillTeams = (stillEvent.extendedProps.team_ids as string[]) ?? [];
  const movingTeams = (movingEvent?.extendedProps.team_ids as string[]) ?? [];
  if (stillTeams.some((id) => movingTeams.includes(id))) return 'team_conflict';

  const stillLoc = (stillEvent.extendedProps.location_id as string | null) ?? null;
  const movingLoc = (movingEvent?.extendedProps.location_id as string | null) ?? null;
  if (!stillLoc || !movingLoc) return null;
  if (stillLoc !== movingLoc) return null;
  const stillZone = (stillEvent.extendedProps.zone_id as string | null) ?? null;
  const movingZone = (movingEvent?.extendedProps.zone_id as string | null) ?? null;
  if (stillZone !== null && movingZone !== null && stillZone !== movingZone) return null;
  return 'zone_conflict';
}

// Post-drop safety net (catches hidden/filtered activities that canEventsOverlap
// never sees). Blocks if the same team is scheduled at the same time, OR if the
// same location+zone is double-booked.
function hasLocationConflictReason(
  eventId: string,
  newStart: Date,
  newEnd: Date,
  extendedProps: Record<string, unknown>,
  activities: import('@/api/activities').Activity[],
): AvailabilityBlockReason | null {
  const team_ids = (extendedProps.team_ids as string[]) ?? [];
  const location_id = extendedProps.location_id as string | null;
  const zone_id = extendedProps.zone_id as string | null;

  for (const a of activities) {
    if (a.id === eventId) continue;
    const aStart = new Date(a.starts_at);
    const aEnd = new Date(a.ends_at);
    if (!(aStart < newEnd && aEnd > newStart)) continue;

    // Same team at same time = always a conflict (regardless of location).
    if (team_ids.some((id) => a.team_ids.includes(id))) return 'team_conflict';

    // Different team: conflict only when same explicit location+zone.
    if (!location_id || !a.location_id || a.location_id !== location_id) continue;
    const aZone = a.zone_id ?? null;
    if (zone_id !== null && aZone !== null && zone_id !== aZone) continue;
    return 'zone_conflict';
  }
  return null;
}
